import React from 'react';
import { createRoot } from 'react-dom/client';
import smoothscroll from 'smoothscroll-polyfill';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { initialize } from '@front/common/src/utils/initialize';
import { errorNotifier, Level, registerErrorNotifier } from '@front/common/src/errors/error-notifier';
import { User } from '@front/common/src/types/commons';
import { isProduction } from '@front/common/src/utils/env';

import { webProSignup } from '@front/i18n';

import { GlobalStyle } from '@front/web-ui/src/styles/global-style';
import { getWebAppLocale, setCountry } from '@front/web-ui/src/utils/country';

import App from './app';
import Config from './utils/config';
import { initializeTracking } from './utils/tracking';

if (process.env.NODE_ENV === 'development' && !window.wecasa) {
  window.wecasa = {
    token: process.env.REACT_APP_AUTH_TOKEN,
    mountpoint: '/',
    siteUrl: process.env.REACT_APP_SITE_URL,
    referer: process.env.REACT_APP_REFERER,
    universe: process.env.REACT_APP_UNIVERSE,
  };
}

initializeTracking(process.env.REACT_APP_AMPLITUDE_API_KEY || '');

smoothscroll.polyfill();

Sentry.init({
  dsn: Config.SENTRY_API_KEY,
  environment: isProduction() ? 'production' : 'staging',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/www\.wecasa\.fr\/api/,
    /^https:\/\/staging\.wecasa\.fr\/api/,
    /^https:\/\/www\.wecasa\.co\.uk\/api/,
    /^https:\/\/staging\.wecasa\.co\.uk\/api/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
  replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
});

registerErrorNotifier({
  notify: (error: Error, level?: Level) => Sentry.captureException(error, { level }),
  setUser: (user: User | null) =>
    user
      ? Sentry.setUser({ id: user.public_id, email: user.email, username: `${user.first_name} ${user.last_name}` })
      : Sentry.setUser(null),
});

/*
 * Here we need to wait for the end of the initialization before we render the components
 * to be sure the translations are correctly initialized before we use them.
 * As the initialization is not inside an async context we cannot use await.
 * This is why we do a then here
 */
initialize({
  appName: Config.APP_NAME,
  authToken: `Bearer ${window.wecasa.token}`,
  baseURL: `${window.wecasa.siteUrl}api/v1`,
  errorNotifier,
  i18nConfig: { translations: webProSignup, locale: getWebAppLocale() },
  setCountry,
}).then(() => {
  const root = createRoot(document.getElementById('subscribe')!);
  root.render(
    <>
      <GlobalStyle />
      <BrowserRouter basename={window.wecasa.mountpoint}>
        <App />
      </BrowserRouter>
    </>,
  );
});
